import * as React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/utils/utils';
import { WithTooltip } from '@/components/ui/tooltip';
import { FOCUS } from '@/components/ui/recipes';
import { ThemedLogo } from '@/modules/theme';
import defaultLogo from '@/assets/lureo_logo.png';

interface HomeLogoProps {
  /** Runs on a plain click instead of following the link (the editor asks before leaving); a middle, modified or right click still opens the link. */
  onClick?: () => void;
  /** Where it goes. */
  href?: string;
  /** Where it goes, as its accessible name (Go to Dashboard). */
  label?: string;
  /** The artwork, when a page shows a variant of the logo (the beta mark). */
  src?: string;
  /** The logo's width: sm in a bar, md in a page's header. */
  size?: 'sm' | 'md';
  className?: string;
  /** Why it can't be followed now (offline), as its tooltip; it is no link meanwhile. */
  disabledReason?: string | null;
}

/** Lureo's logo as the way home: the brand mark drawn in the theme's logo style, a link to the dashboard that opens in a new tab like any other. */
export function HomeLogo({ onClick, href = '/dashboard', label = 'Go to Dashboard', src = defaultLogo, size = 'md', className, disabledReason }: HomeLogoProps) {
  if (disabledReason) {
    return (
      <WithTooltip content={disabledReason}>
        <span aria-disabled="true" aria-label={label} className={cn('inline-flex shrink-0 cursor-not-allowed items-center justify-center rounded-lureo-radius-pill px-2 py-1 opacity-lureo-disabled-opacity', className)}>
          <ThemedLogo src={src} alt="" className={cn('pointer-events-none', size === 'sm' ? 'w-14' : 'w-24')} />
        </span>
      </WithTooltip>
    );
  }
  return (
    <WithTooltip content={label}>
      <Link
        to={href}
        aria-label={label}
        draggable={false}
        onClick={event => {
          if (!onClick || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          onClick();
        }}
        className={cn('inline-flex shrink-0 items-center justify-center rounded-lureo-radius-pill px-2 py-1 transition-opacity hover:opacity-80', FOCUS, className)}
      >
        {/* The link, not the picture, takes a right click, so the menu offers the link */}
        <ThemedLogo src={src} alt="" className={cn('pointer-events-none', size === 'sm' ? 'w-14' : 'w-24')} />
      </Link>
    </WithTooltip>
  );
}
